import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { adminAPI, authAPI } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { BarChart3, FileText, Users, Building, Settings, LogOut, MessageSquare, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Loader } from '@/components/ui/loader';

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const userRes = await authAPI.getMe();
      setUser(userRes.data);
      fetchStats();
    } catch (error) {
      console.error('Auth error:', error);
      navigate('/admin/login');
    }
  }, [navigate]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const fetchStats = async () => {
    try {
      const res = await adminAPI.getDashboardStats();
      setStats(res.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Loader className="text-secondary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 admin-panel">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-heading font-bold">KAPISH Admin Dashboard</h1>
              <p className="text-sm text-gray-600">Welcome, {user?.email}</p>
            </div>
            <Button
              data-testid="logout-button"
              onClick={handleLogout}
              variant="outline"
              className="flex items-center gap-2"
            >
              <LogOut size={18} />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div data-testid="dashboard-stats" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Leads</p>
                <p className="text-3xl font-bold">{stats?.total_leads || 0}</p>
              </div>
              <MessageSquare size={32} className="text-blue-500" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Monthly Leads</p>
                <p className="text-3xl font-bold">{stats?.monthly_leads || 0}</p>
              </div>
              <BarChart3 size={32} className="text-green-500" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Contacted</p>
                <p className="text-3xl font-bold">{stats?.contacted_leads || 0}</p>
              </div>
              <Users size={32} className="text-purple-500" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Pending</p>
                <p className="text-3xl font-bold">{stats?.pending_leads || 0}</p>
              </div>
              <FileText size={32} className="text-orange-500" />
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-xl font-heading font-semibold mb-6">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Link to="/admin/leads">
              <Button
                data-testid="manage-leads-btn"
                className="w-full justify-start bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200"
                variant="ghost"
              >
                <MessageSquare className="mr-2" size={20} />
                Manage Leads
              </Button>
            </Link>

            <Link to="/admin/content">
              <Button
                data-testid="manage-content-btn"
                className="w-full justify-start bg-green-50 text-green-700 hover:bg-green-100 border border-green-200"
                variant="ghost"
              >
                <FileText className="mr-2" size={20} />
                Manage Content
              </Button>
            </Link>

            <Link to="/admin/partners">
              <Button
                data-testid="manage-partners-btn"
                className="w-full justify-start bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200"
                variant="ghost"
              >
                <Users className="mr-2" size={20} />
                Manage Partners
              </Button>
            </Link>

            <Link to="/admin/services">
              <Button
                data-testid="manage-services-btn"
                className="w-full justify-start bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border border-yellow-200"
                variant="ghost"
              >
                <Building className="mr-2" size={20} />
                Manage Services
              </Button>
            </Link>

            <Link to="/admin/settings">
              <Button
                data-testid="settings-btn"
                className="w-full justify-start bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200"
                variant="ghost"
              >
                <Settings className="mr-2" size={20} />
                Settings
              </Button>
            </Link>
            <Link to="/admin/contact-info">
              <Button
                data-testid="contact-info-btn"
                className="w-full justify-start bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200"
                variant="ghost"
              >
                <Mail className="mr-2" size={18} />
                Contact Info
              </Button>
            </Link>
            <a href="/" target="_blank" rel="noopener noreferrer">
              <Button
                className="w-full justify-start bg-secondary/10 text-secondary hover:bg-secondary/20 border border-secondary/20"
                variant="ghost"
              >
                View Website
              </Button>
            </a>
          </div>
        </div>
      </main>
    </div>
  );
};
